import { useMemo, useState } from "react";
import classNames from "./UsageGraph.module.css";
import { ResponsiveBarCanvas } from "@nivo/bar";
import { useTimeIntervals } from "../../../../hooks/usage";
import { TimeRange } from "./TimeRangeSelect";
import { getLanguageName } from "../../../../types";
import Select from "../../../common/Select";

export default function LanguageUsageGraph(props: {
  timeRange: TimeRange;
  languageUsageIntervals: {
    language: string;
    intervals: {
      start: number;
      end: number;
    }[];
  }[];
}) {
  const [selectedLanguage, setSelectedLanguage] = useState<string>();

  const timeIntervals = useTimeIntervals(props.timeRange);

  const data = useMemo(() => {
    // Create fresh array with copies of timeIntervals
    const initial = timeIntervals.map((t) => ({ interval: t, minutes: 0 }));

    // Add number of minutes specific interval was active
    const addIntervalTime = (
      acc: { interval: (typeof timeIntervals)[number]; minutes: number }[],
      interval: { start: number; end: number }
    ) => {
      const activeIntervals = timeIntervals.filter(
        (i) => interval.start <= i.end && interval.end >= i.start
      );

      activeIntervals.forEach((activeInterval) => {
        const minutes =
          (Math.min(activeInterval.end, interval.end) -
            Math.max(activeInterval.start, interval.start)) /
          (1000 * 60);

        const existing = acc.find((item) => item.interval === activeInterval);
        if (existing) {
          existing.minutes += minutes;
        }
      });

      return acc;
    };

    // === Case 1: Selected language - use specific language intervals
    if (selectedLanguage) {
      const usage = props.languageUsageIntervals.find(
        (u) => u.language === selectedLanguage
      );

      if (!usage)
        return initial.map((d) => ({
          interval: d.interval.name,
          minutes: d.minutes.toFixed(2),
        }));

      const result = usage.intervals.reduce(
        (acc, interval) => {
          return addIntervalTime(acc, interval);
        },
        initial.map((d) => ({ ...d }))
      );

      return result
        .sort((a, b) => a.interval.start - b.interval.start)
        .map((d) => ({
          interval: d.interval.name,
          minutes: d.minutes.toFixed(2),
        }));
    }

    // === Case 2: No selected language - merge all language intervals (should be same as total usage for timespan)
    const allIntervals = props.languageUsageIntervals.flatMap(
      (lang) => lang.intervals
    );

    const merged = allIntervals
      .sort((a, b) => a.start - b.start)
      .reduce((acc: typeof allIntervals, curr) => {
        const last = acc[acc.length - 1];
        if (last && curr.start <= last.end) {
          last.end = Math.max(last.end, curr.end);
        } else {
          acc.push({ ...curr });
        }
        return acc;
      }, []);

    const result = merged.reduce(
      (acc, interval) => {
        return addIntervalTime(acc, interval);
      },
      initial.map((d) => ({ ...d }))
    );

    return result
      .sort((a, b) => a.interval.start - b.interval.start)
      .map((d) => ({
        interval: d.interval.name,
        minutes: d.minutes.toFixed(2),
      }));
  }, [props.languageUsageIntervals, selectedLanguage, timeIntervals]);

  const languages = useMemo(() => {
    return props.languageUsageIntervals.map((usage) => usage.language);
  }, [props.languageUsageIntervals]);

  return (
    <div className={classNames.UsageGraph}>
      <div className={classNames.Header}>
        <h3>Total minutes translated</h3>
        {languages.length >= 2 && (
          <Select
            items={[
              { value: undefined, label: "All languages" },
              ...languages.map((l) => ({
                value: l,
                label: getLanguageName(l)!,
              })),
            ]}
            selected={selectedLanguage}
            onChange={(value) => setSelectedLanguage(value as string)}
            align="end"
            color="gray"
            backgroundColor="black"
            borderColor="dark-gray"
          />
        )}
      </div>
      <div>
        <ResponsiveBarCanvas
          data={data}
          keys={["minutes"]}
          indexBy="interval"
          margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
          padding={0.7}
          colors="#3c75ee"
          axisLeft={{ format: (i) => (Math.floor(i) === i ? i : "") }}
          axisBottom={{
            format: (interval: string) => {
              const index = data.findIndex((d) => d.interval === interval);
              return index === 0 || index === data.length - 1 ? interval : "";
            },
          }}
          enableLabel={false}
          legendLabel={() => "Usage in minutes"}
          tooltip={({ value, data }) => (
            <>
              {data.interval}: {Math.round(value)} minutes
            </>
          )}
          theme={{
            grid: {
              line: {
                stroke: "#555",
              },
            },
            axis: {
              ticks: {
                text: {
                  fill: "#ccc",
                },
              },
              legend: {
                text: {
                  fill: "#ccc",
                },
              },
            },
            tooltip: {
              container: {
                color: "#ccc",
              },
            },
          }}
          legends={[
            {
              dataFrom: "keys",
              itemTextColor: "#ccc",
              anchor: "top-left", // Position the legend in the top left
              direction: "row", // Place the legend items in a row
              justify: false,
              translateX: -20,
              translateY: -40, // Adjust the position to avoid overlap with bars
              itemsSpacing: 10, // Space between legend items
              itemWidth: 100,
              itemHeight: 20,
              symbolSize: 12,
              effects: [
                {
                  on: "hover",
                  style: {
                    itemOpacity: 0.85,
                  },
                },
              ],
            },
          ]}
        />
      </div>
    </div>
  );
}
